import {TFunction} from 'i18next'
import {useTranslation} from 'react-i18next'
import {v4 as uuidv4} from 'uuid'
import {parseBackendError} from '../../utils/parseBackendError.ts'
import {ConversionCta, CRM_ERROR_PAYMENT_PROVIDER, CRM_ERROR_PRICING_NOT_CONFIGURED} from '../../domain/entities/ConversionPipeline.ts'
import {
    CRM_ERROR_COMMAND_NOT_ALLOWED,
    CRM_ERROR_INVALID_COMMAND,
    CRM_ERROR_NOT_IN_PIPELINE,
    HistoryEvent,
    NextAction,
    TeamMember,
} from '../../domain/entities/Pipeline.ts'

// Non-component helpers for the Pipelines UI (kept out of the .tsx files so
// React Fast Refresh keeps working).

// Accent tones the app palette lacks (taken from the design).
export const TONE = {
    red: 'bg-[#FDECEA] text-[#B42318]',
    amber: 'bg-[#FFF4D6] text-[#8A5A00]',
    grey: 'bg-neutralBlack-100 text-neutralBlack-700',
    teal: 'bg-[#E3EEEF] text-[#14343B]',
    green: 'bg-[#E3F3EA] text-[#1F6B4F]',
} as const

// Markets offered when the agencies facets are not loaded yet.
export const DEFAULT_MARKETS = ['US', 'ES', 'UK', 'IT']

export const FIELD_CLASS = 'w-full h-10 px-3 rounded-[10px] border border-neutralBlack-300 bg-white text-[13.5px] text-neutralBlack-900 focus:outline-none focus:border-neutralBlack-700'

export const SECTION_LABEL = 'text-[11px] font-semibold tracking-[.06em] uppercase text-neutralBlack-600'

const DAY_MS = 24 * 60 * 60 * 1000

export const newCommandId = () => uuidv4()

type NamedTraveler = {firstName?: string | null; lastName?: string | null; email?: string | null; phone?: string | null}

export const travelerName = (traveler: NamedTraveler, fallback: string) =>
    [traveler.firstName, traveler.lastName].filter(Boolean).join(' ') || traveler.email || traveler.phone || fallback

const startOfUtcDay = (millis: number) => {
    const d = new Date(millis)
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

// Whole days from today (local) to `millis`: 0 today, 1 tomorrow, -1 yesterday.
export function daysFromToday(millis: number, now = Date.now()) {
    const today = new Date(now)
    const target = new Date(millis)
    const a = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
    const b = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate())
    return Math.round((b - a) / DAY_MS)
}

// "D-5" before the trip, "Day 3" during it, "F+12" after it (F = trip end).
// Trip dates are UTC midnight.
export function dayCounter(trip: {startDate: number | null; endDate: number | null}, t: TFunction, now = Date.now()): string | null {
    if (trip.startDate == null || trip.endDate == null) return null
    const today = startOfUtcDay(now)
    const toStart = Math.round((trip.startDate - today) / DAY_MS)
    const sinceEnd = Math.round((today - trip.endDate) / DAY_MS)
    if (toStart > 0) return t('pipelines.counter.before', {count: toStart})
    if (sinceEnd > 0) return t('pipelines.counter.after', {count: sinceEnd})
    return t('pipelines.counter.during', {count: 1 - toStart})
}

// Relative due date for a footer or a task row, never a calendar date.
export function relativeDue(dueAt: number | null | undefined, t: TFunction, now = Date.now()): {text: string; overdue: boolean} | null {
    if (dueAt == null) return null
    const overdue = dueAt < now
    const days = daysFromToday(dueAt, now)
    if (days === 0) return {text: overdue ? t('pipelines.due.overdueToday') : t('pipelines.due.today'), overdue}
    if (days === 1) return {text: t('pipelines.due.tomorrow'), overdue}
    if (days > 1) return {text: t('pipelines.due.inDays', {count: days}), overdue}
    return {text: t('pipelines.due.overdueDays', {count: -days}), overdue: true}
}

// The backend sends an English `text`; the portal may translate by `key`.
export const touchLabel = (key: string | null | undefined, text: string | null | undefined, t: TFunction) =>
    key ? t(`pipelines.touch.${key}`, {defaultValue: text || key}) : (text || t('pipelines.touch.WAIT'))

// Activation waits have no key ("Waiting for sign-up"); Conversion ones say
// what they wait for (WAIT_<stage> or ON_HOLD, its contract §2).
export const nextActionLabel = (nextAction: NextAction, t: TFunction) =>
    nextAction.type === 'WAIT' && !nextAction.key
        ? t('pipelines.touch.WAIT', {defaultValue: nextAction.text})
        : touchLabel(nextAction.key, nextAction.text, t)

// Amounts travel in minor units with their ISO 4217 code (Conversion §0).
export function formatMoney(amountMinor: number | null | undefined, currency: string | null | undefined, locale?: string): string | null {
    if (amountMinor == null || !currency) return null
    try {
        const format = new Intl.NumberFormat(locale, {style: 'currency', currency})
        const digits = format.resolvedOptions().maximumFractionDigits ?? 2
        return format.format(amountMinor / 10 ** digits)
    } catch {
        return `${(amountMinor / 100).toFixed(2)} ${currency}`
    }
}

export function useDateFormatters() {
    const {i18n} = useTranslation('dashboard')
    const locale = i18n.language
    const short = new Intl.DateTimeFormat(locale, {day: 'numeric', month: 'short', timeZone: 'UTC'})
    return {
        // "12-24 Mar" or "28 Dec - 9 Jan" (trip dates, UTC).
        formatTripRange: (start: number | null, end: number | null) => {
            if (start == null || end == null) return '—'
            const s = new Date(start), e = new Date(end)
            if (s.getUTCMonth() === e.getUTCMonth() && s.getUTCFullYear() === e.getUTCFullYear()) {
                const month = new Intl.DateTimeFormat(locale, {month: 'short', timeZone: 'UTC'}).format(e)
                return `${s.getUTCDate()}-${e.getUTCDate()} ${month}`
            }
            return `${short.format(s)} - ${short.format(e)}`
        },
        formatDay: (millis: number | null | undefined) => millis == null ? '—'
            : new Intl.DateTimeFormat(locale, {day: '2-digit', month: 'short', year: 'numeric'}).format(new Date(millis)),
        formatDateTime: (millis: number | null | undefined) => millis == null ? '—'
            : new Intl.DateTimeFormat(locale, {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}).format(new Date(millis)),
    }
}

// <input type="date"> value → epoch millis at 09:00 local time that day, the
// time a person-scheduled touch (Will do it, follow-up) is due.
export function dateInputToMillis(value: string): number | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (!match) return null
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 9, 0, 0).getTime()
}

// <input type="date"> value → UTC midnight, the unit of trip dates (board from/to).
export function dateInputToUtcMidnight(value: string): number | undefined {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (!match) return undefined
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

export function millisToDateInput(millis: number | null | undefined, utc = false): string {
    if (millis == null) return ''
    const d = new Date(millis)
    const y = utc ? d.getUTCFullYear() : d.getFullYear()
    const m = (utc ? d.getUTCMonth() : d.getMonth()) + 1
    const day = utc ? d.getUTCDate() : d.getDate()
    return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export const tomorrowDateInput = () => millisToDateInput(Date.now() + DAY_MS)

export const memberById = (team: TeamMember[], id: string | null | undefined) =>
    id ? team.find((member) => member.id === id) ?? null : null

// §4 error keys. COMMAND_NOT_ALLOWED carries the reason in `message`.
export function crmErrorMessage(error: unknown, t: TFunction): string {
    const fallback = t('pipelines.error.generic')
    const {errorKey, message} = parseBackendError(error, fallback)
    if (errorKey === CRM_ERROR_COMMAND_NOT_ALLOWED) return t('pipelines.error.notAllowed', {reason: message})
    if (errorKey === CRM_ERROR_INVALID_COMMAND) return t('pipelines.error.invalid', {reason: message})
    if (errorKey === CRM_ERROR_NOT_IN_PIPELINE) return t('pipelines.error.notInPipeline')
    if (errorKey === CRM_ERROR_PRICING_NOT_CONFIGURED) return t('pipelines.error.pricingNotConfigured')
    if (errorKey === CRM_ERROR_PAYMENT_PROVIDER) return t('pipelines.error.paymentProvider')
    return fallback
}

export const cardTravelerName = (card: {traveler: NamedTraveler}, t: TFunction) => travelerName(card.traveler, t('pipelines.card.noName'))

const str = (value: unknown) => (value == null ? '' : String(value))

// One history event as a sentence (§3.3). Unknown types fall back to the raw type.
export function historyText(event: HistoryEvent, t: TFunction, formatDay: (millis: number | null | undefined) => string, team: TeamMember[] = []): string {
    const data = event.data ?? {}
    const channel = data.channel ? t(`pipelines.channel.${str(data.channel)}`, {defaultValue: str(data.channel)}) : ''
    const touch = data.key ? touchLabel(str(data.key), null, t) : ''
    const stageName = (value: unknown) => (value ? t(`pipelines.stage.${str(value)}`, {defaultValue: str(value)}) : '—')
    const values = {
        touch,
        channel,
        status: data.status ? t(`activation.touchStatus.${str(data.status)}`, {defaultValue: str(data.status)}) : '',
        outcome: data.outcome ? t(`pipelines.outcome.${str(data.outcome)}`, {defaultValue: str(data.outcome)}) : '',
        from: stageName(data.from),
        to: stageName(data.to),
        reason: data.reason
            ? t(`pipelines.lostReason.${str(data.reason)}`, {defaultValue: t(`pipelines.pendingReason.${str(data.reason)}`, {defaultValue: str(data.reason)})})
            : '',
        decision: data.decision ? t(`pipelines.decision.${str(data.decision)}`, {defaultValue: str(data.decision)}) : '',
        pages: str(data.pages),
        url: str(data.url ?? data.trackingUrl),
        editor: str(data.editorName) || memberById(team, str(data.editorId) || null)?.name || t('pipelines.unassigned'),
        printChannel: str(data.printChannel),
        value: data.value === true ? t('pipelines.yes') : data.value === false ? t('pipelines.no') : str(data.value),
        draftNumber: str(data.draftNumber),
        amount: typeof data.amountMinor === 'number' ? formatMoney(data.amountMinor, str(data.currency)) ?? '' : '',
        date: typeof data.date === 'number' ? formatDay(data.date) : '',
        until: typeof data.until === 'number' ? formatDay(data.until) : '',
        text: str(data.text),
        note: str(data.note),
        assignee: str(data.assigneeName) || memberById(team, str(data.assignee) || null)?.name || str(data.assignee) || t('pipelines.unassigned'),
        contact: [data.email, data.phone].filter(Boolean).map(str).join(' · '),
        command: str(data.type ?? data.command),
    }
    const base = t(`pipelines.history.${event.type}`, {...values, defaultValue: event.type})
    const notedInText = event.type === 'lost' || event.type === 'note_added' || event.type === 'stage_overridden' || event.type === 'on_hold'
    return values.note && !notedInText ? `${base} · ${values.note}` : base
}

// Keys the portal has its own copy for; anything else shows the server label.
const KNOWN_CTA_KEYS = new Set([
    'DRAFT_READY', 'DETAILS_PENDING', 'BACK_TO_PRODUCTION', 'SEND_DRAFT', 'REQUEST_CHANGES', 'APPROVE', 'ADAPT_51T',
    'CUSTOMER_EDITS', 'PAYS', 'ADAPTED', 'START_FOR_CUSTOMER', 'CREATE_PAYMENT_LINK', 'MARK_SENT_TO_PRINT', 'MARK_SHIPPED', 'MARK_DELIVERED',
])

export const ctaLabel = (cta: ConversionCta, t: TFunction) =>
    KNOWN_CTA_KEYS.has(cta.key) ? t(`pipelines.cta.${cta.key}`, {defaultValue: cta.label || cta.key}) : (cta.label || cta.key)
